import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';

import { MessageTemplate } from '../../services/message-template';
import { NotificationService, SendTemplateMessageRequest } from '../../services/notification';
import { FileService, UploadedFile, FILE_RULES } from '../../services/file';
import { DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';

/**
 * Compose dialog: render a MessageTemplate, optionally attach a file, and queue it —
 * `api/File/Upload` first, then `api/Notification/SendTemplate` with the upload's KEY
 * (the durable pointer the WhatsApp sender re-presigns at send time; a presigned URL
 * captured now would be stale on a delivery retry). Admin-only, like the endpoint.
 *
 * Opened via `open(templateKey?)` — from the Templates toolbar (blank) or a row's
 * "Send test" action (preselected).
 */
@Component({
  selector: 'app-send-message-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    DatePickerModule,
    TooltipModule,
  ],
  templateUrl: './send-message-dialog.html',
  styleUrls: ['./send-message-dialog.css'],
})
export class SendMessageDialog {
  private notifications = inject(NotificationService);
  private files = inject(FileService);
  private toast = inject(ToastService);

  /** All templates known to the parent list — drives the key select + placeholder inputs.
   *  Signal inputs, so the computeds below re-evaluate when the parent's data arrives. */
  templates = input<MessageTemplate[]>([]);
  partnerOptions = input<DropdownItem[]>([]);
  clientOptions = input<DropdownItem[]>([]);
  /** Fired after a successful queue, so the parent can e.g. point at the outbox. */
  sent = output<string[]>();

  readonly acceptTypes = FILE_RULES.allowed.join(',');

  show = signal(false);
  sending = signal(false);
  uploading = signal(false);

  key = signal<string>('');
  chWhatsapp = signal(false);
  chSms = signal(false);
  chEmail = signal(false);
  mobile = signal('');
  email = signal('');
  partnerId = signal<number | null>(null);
  clientId = signal<number | null>(null);
  messageType = signal('');
  schedule = signal<Date | null>(null);
  /** Values typed for the current template's placeholders, keyed by name. */
  values = signal<Record<string, string>>({});
  file = signal<UploadedFile | null>(null);

  /** Distinct template keys, for the select. */
  keyOptions = computed(() => {
    const seen = new Set<string>();
    for (const t of this.templates()) if (t.templateKey) seen.add(t.templateKey);
    if (this.key() && !seen.has(this.key())) seen.add(this.key());
    return [...seen].sort();
  });

  /**
   * Placeholder names for the selected key — the union across that key's channel variants,
   * in first-appearance order. {{Company}} is excluded: the server fills it automatically.
   */
  placeholderNames = computed(() => {
    const k = this.key();
    if (!k) return [] as string[];
    const names: string[] = [];
    for (const t of this.templates()) {
      if (t.templateKey !== k) continue;
      for (const text of [t.body ?? '', t.subject ?? '']) {
        for (const m of text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
          if (m[1] !== 'Company' && !names.includes(m[1])) names.push(m[1]);
        }
      }
    }
    return names;
  });

  anyChannel = computed(() => this.chWhatsapp() || this.chSms() || this.chEmail());
  /** With no channel ticked the server resolves them from the message type instead. */
  needsMobile = computed(() => this.chWhatsapp() || this.chSms() || !this.anyChannel());
  needsEmail = computed(() => this.chEmail() || !this.anyChannel());

  open(templateKey?: string | null) {
    this.key.set(templateKey ?? '');
    this.chWhatsapp.set(false);
    this.chSms.set(false);
    this.chEmail.set(false);
    this.mobile.set('');
    this.email.set('');
    this.partnerId.set(null);
    this.clientId.set(null);
    this.messageType.set(templateKey ?? '');
    this.schedule.set(null);
    this.values.set({});
    this.file.set(null);
    this.show.set(true);
  }

  close() {
    this.show.set(false);
  }

  onKeyChange(k: string) {
    this.key.set(k ?? '');
    // The type classifies the outbox row (and resolves channels when none are ticked);
    // the key is the natural default until the admin overrides it.
    this.messageType.set(k ?? '');
  }

  setValue(name: string, v: string) {
    this.values.update((cur) => ({ ...cur, [name]: v }));
  }

  onPick(list: FileList | null) {
    const f = list?.[0];
    if (!f) return;
    const err = this.files.validate(f);
    if (err) {
      this.toast.warning(err);
      return;
    }
    this.uploading.set(true);
    this.files.upload(f, 'Messaging').subscribe({
      next: (up) => {
        this.uploading.set(false);
        this.file.set(up);
      },
      error: (e) => {
        this.uploading.set(false);
        this.toast.error(e?.message || 'Upload failed');
      },
    });
  }

  clearFile() {
    this.file.set(null);
  }

  send() {
    const key = this.key().trim();
    if (!key) {
      this.toast.warning('Pick a template to send.');
      return;
    }

    const mobile = this.mobile().trim();
    const email = this.email().trim();
    if (!mobile && !email) {
      this.toast.warning('A mobile number or an email address is required.');
      return;
    }
    if (this.anyChannel()) {
      if ((this.chWhatsapp() || this.chSms()) && !mobile) {
        this.toast.warning('WhatsApp / SMS need a mobile number.');
        return;
      }
      if (this.chEmail() && !email) {
        this.toast.warning('Email needs an email address.');
        return;
      }
    } else if (!this.messageType().trim()) {
      this.toast.warning('Tick at least one channel, or give a message type to resolve them from.');
      return;
    }

    const channels: string[] = [];
    if (this.chWhatsapp()) channels.push('WHATSAPP');
    if (this.chSms()) channels.push('SMS');
    if (this.chEmail()) channels.push('EMAIL');

    const placeholders: Record<string, string> = {};
    const vals = this.values();
    for (const name of this.placeholderNames()) {
      const v = (vals[name] ?? '').trim();
      if (v) placeholders[name] = v;
    }

    const up = this.file();
    const req: SendTemplateMessageRequest = {
      templateKey: key,
      channels,
      mobile: mobile || null,
      email: email || null,
      partnerId: this.partnerId(),
      clientId: this.clientId(),
      placeholders,
      messageType: this.messageType().trim() || null,
      // The KEY, not the presigned url — see the class comment.
      mediaStorageKey: up?.key || null,
      mediaFileName: up?.fileName || null,
      mediaMimeType: up?.contentType || null,
      scheduledAt: this.schedule() ? this.schedule()!.toISOString() : null,
    };

    this.sending.set(true);
    this.notifications.sendTemplate(req).subscribe({
      next: (res: any) => {
        this.sending.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to queue the message.');
          return;
        }
        const queued: string[] = res?.data?.channels ?? res?.data?.Channels ?? channels;
        this.toast.success(
          queued.length
            ? `Queued on ${queued.join(', ')} — delivery shows in the Outbox tab.`
            : res?.message || 'Message queued.',
        );
        this.sent.emit(queued);
        this.close();
      },
      error: () => this.sending.set(false),
    });
  }
}
